import React, { useState } from "react";
import { Mail, Send, CheckCircle2 } from "lucide-react";
import { postContact } from "@/lib/api";
import { RESEARCH_NOTICE } from "@/lib/store";
import { useSEO, breadcrumbSchema } from "@/lib/seo";
import PageHeader from "@/components/PageHeader";
import { Reveal } from "@/components/motion/Reveal";
import { toast } from "sonner";

const initial = { name: "", email: "", subject: "", order_number: "", message: "" };

const encode = (data) =>
  Object.keys(data)
    .map((k) => encodeURIComponent(k) + "=" + encodeURIComponent(data[k]))
    .join("&");

export default function Contact() {
  const [form, setForm] = useState(initial);
  const [botField, setBotField] = useState("");
  const [loading, setLoading] = useState(false);
  const [sent, setSent] = useState(false);

  const update = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    const payload = { "form-name": "contact", "bot-field": botField, ...form };

    // Fire Netlify Forms first — POSTing to "/" is intercepted by Netlify's
    // form handler on the deployed site. On preview/local it 404s and we fall
    // through to the backend, which still stores the message + emails us.
    let netlifyOk = false;
    try {
      const res = await fetch("/", {
        method: "POST",
        headers: { "Content-Type": "application/x-www-form-urlencoded" },
        body: encode(payload),
      });
      netlifyOk = res.ok;
    } catch (_) {
      /* not on Netlify — continue */
    }

    // Always persist to the backend so we have a canonical record + inbox copy
    // via Resend, regardless of whether Netlify accepted the submission.
    try {
      await postContact(form);
      setSent(true);
      setForm(initial);
      toast.success(
        netlifyOk
          ? "Message sent — thank you."
          : "Message received — thank you."
      );
    } catch {
      toast.error("Something went wrong. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  useSEO({
    title: "Contact | Renova Peptide Co.",
    description: "Contact Renova Peptide Co. with product or order questions. Our team is happy to help.",
    path: "/contact",
    jsonLd: breadcrumbSchema([{ name: "Home", path: "/" }, { name: "Contact", path: "/contact" }]),
  });

  return (
    <>
      <PageHeader
        overline="Get in Touch"
        title="Contact Renova"
        intro="Questions about a product or an order? Send us a message and our team will respond as promptly as possible."
        breadcrumbs={[{ name: "Home", path: "/" }, { name: "Contact", path: "/contact" }]}
      />

      <section className="container-rnv py-16 md:py-20">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-12">
          <div className="lg:col-span-5">
            <Reveal>
              <h2 className="font-serif text-3xl text-[#1E2A44]">We are here to help</h2>
              <p className="mt-4 text-[#262E3D] leading-relaxed">
                For order-specific questions, please include your order number so our team can
                respond as promptly as possible.
              </p>
              <ul className="mt-8 space-y-5">
                <ContactRow
                  icon={Mail}
                  label="Email"
                  value="Renovapeptideco@gmail.com"
                  href="mailto:Renovapeptideco@gmail.com"
                />
              </ul>
              <p className="mt-8 text-xs text-[#85827D]">{RESEARCH_NOTICE}</p>
            </Reveal>
          </div>

          <div className="lg:col-span-7">
            <Reveal delay={0.1}>
              {sent ? (
                <div className="bg-white border border-[#C8A96A]/40 rounded-sm p-10 text-center" data-testid="contact-success">
                  <CheckCircle2 size={44} className="mx-auto text-[#C8A96A]" strokeWidth={1.5} />
                  <h3 className="font-serif text-2xl text-[#1E2A44] mt-5">Thank you</h3>
                  <p className="mt-2 text-sm text-[#85827D]">Your message has been received. We will be in touch shortly.</p>
                  <button onClick={() => setSent(false)} className="btn-gold mt-6">Send another message</button>
                </div>
              ) : (
                <form
                  onSubmit={submit}
                  name="contact"
                  method="POST"
                  data-netlify="true"
                  data-netlify-honeypot="bot-field"
                  className="bg-white border border-[#1E2A44]/10 rounded-sm p-8 md:p-10"
                  data-testid="contact-form"
                >
                  <input type="hidden" name="form-name" value="contact" />
                  <p className="hidden" aria-hidden="true">
                    <label>
                      Do not fill this out:{" "}
                      <input name="bot-field" value={botField} onChange={(e) => setBotField(e.target.value)} tabIndex={-1} autoComplete="off" />
                    </label>
                  </p>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
                    <Field label="Name" required>
                      <input required value={form.name} onChange={update("name")} name="name" className="rnv-input" data-testid="contact-name" />
                    </Field>
                    <Field label="Email" required>
                      <input required type="email" value={form.email} onChange={update("email")} name="email" className="rnv-input" data-testid="contact-email" />
                    </Field>
                  </div>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-5 mt-5">
                    <Field label="Subject">
                      <input value={form.subject} onChange={update("subject")} name="subject" className="rnv-input" data-testid="contact-subject" />
                    </Field>
                    <Field label="Order Number (optional)">
                      <input value={form.order_number} onChange={update("order_number")} name="order_number" className="rnv-input" data-testid="contact-order" />
                    </Field>
                  </div>
                  <div className="mt-5">
                    <Field label="Message" required>
                      <textarea required rows={6} value={form.message} onChange={update("message")} name="message" className="rnv-input resize-none" data-testid="contact-message" />
                    </Field>
                  </div>
                  <button type="submit" disabled={loading} className="btn-navy mt-6 w-full sm:w-auto justify-center" data-testid="contact-submit">
                    {loading ? "Sending…" : "Send Message"} <Send size={16} />
                  </button>
                </form>
              )}
            </Reveal>
          </div>
        </div>
      </section>
    </>
  );
}

function ContactRow({ icon: Icon, label, value, href }) {
  const inner = (
    <>
      <span className="w-10 h-10 rounded-sm border border-[#C8A96A]/60 flex items-center justify-center text-[#C8A96A] shrink-0">
        <Icon size={18} strokeWidth={1.6} />
      </span>
      <div>
        <p className="overline">{label}</p>
        <p className="text-sm text-[#262E3D] mt-1">{value}</p>
      </div>
    </>
  );
  return (
    <li className="flex items-start gap-4">
      {href ? (
        <a href={href} className="flex items-start gap-4 hover:opacity-80" data-testid={`contact-${label.toLowerCase()}-link`}>
          {inner}
        </a>
      ) : (
        inner
      )}
    </li>
  );
}

function Field({ label, required, children }) {
  return (
    <label className="block">
      <span className="text-xs uppercase tracking-[0.14em] text-[#85827D]">
        {label}{required && <span className="text-[#C8A96A]"> *</span>}
      </span>
      <div className="mt-2">{children}</div>
    </label>
  );
}
